// @flow

import React, { Component } from "react";
import classNames from "classnames";
import { Label, Col } from "reactstrap";
import Checkbox from "../../checkbox";
import type { CheckboxWidget as CheckboxWidgetProps } from "react-jsonschema-form/lib/components/widgets/CheckboxWidget";
import Icon from "../../icon";

type Option = { value: string | number, label: string };

type Props = $Exact<{
  ...CheckboxWidgetProps,
  options: Object, // label must be placed in uiSchema > "ui:options" > label
}>;

type State = $Exact<{
  values: Array<string | number>,
  options: Array<Option>,
}>;

/**
 * Usage
 * In the schema you should put uniqueItems: true, otherwise will load the arrayTemplate instead of this widget
 *  schema example :
                      daysWeekly: {
                        type: "array",
                        uniqueItems: true,
                        items: {
                          enum: ["monday","tuesday","wednesday","thursday","friday","saturday","sunday",],
                        },
                      },
 uiSchema example :
                       daysWeekly: {
                        "ui:widget": "checkboxes",
                        "ui:options": {
                          label: "On which day do you want to receive the report?",
                          labelposition: "left",
                          required: true,
                          capitalizeLabel: true,
                        },
                      },

 Working example : /src/modules/reporting/components/schedule-reports-settings-modal/index.js
 */

export class CheckboxesWidget extends Component<Props, State> {
  state = {
    options: [],
    values: [],
  };

  componentDidMount() {
    const options = this.props.options.enumOptions.map(val => {
      const label = this.props.options.capitalizeLabel
        ? val.label.charAt(0).toUpperCase() + val.label.slice(1)
        : val.label;
      return {
        value: val.value,
        label,
      };
    });
    this.setState({ options, values: this.props.value });
  }

  handleChange = (event: SyntheticEvent<HTMLInputElement>) => {
    const values = [...this.state.values];
    const value = event.currentTarget.value;
    const valPos = values.indexOf(value);
    const isChecked = valPos > -1;

    if (!isChecked) {
      values.push(value);
    } else {
      values.splice(valPos, 1);
    }
    this.setState({ values });

    return this.props.onChange(values);
  };

  render() {
    const props = Object.assign({}, this.props, {
      readOnly: this.props.readonly,
      autoFocus: this.props.autofocus,
      onChange: this.handleChange,
    });

    const { label, labelposition, labelclass, required } = props.options;

    delete props.readonly;
    delete props.formContext;
    delete props.autofocus;
    delete props.rawErrors;
    delete props.registry;
    delete props.schema;
    delete props.labelposition;
    delete props.enumOptions;

    return (
      <div
        className={classNames("justify-content-center form-group d-flex", {
          "flex-column": !labelposition,
          "flex-row": labelposition === "left",
          "flex-row-reverse": labelposition === "right",
        })}
      >
        {label && (
          <Col className={"col-xs-12 px-0"}>
            <Label className={`${labelclass}`}>
              {label}{" "}
              {required && (
                <Icon
                  icon="asterisk"
                  size="xs"
                  className={classNames("text-warning", {
                    "ml-2": props.displayLabel && props.label,
                  })}
                />
              )}
            </Label>
          </Col>
        )}
        <Col>
          {this.state.options.map((option: Option) => {
            const id = props.id + option.value;
            return (
              <Checkbox
                key={option.value}
                {...Object.assign({}, props, option, {
                  id: `${id}`,
                  name: `${id}`,
                  className: classNames("mt-1", props.className),
                  checked: this.state.values.indexOf(option.value) > -1,
                })}
              />
            );
          })}
        </Col>
      </div>
    );
  }
}

export default CheckboxesWidget;
